import { useMemo } from 'react';
import { useParams } from 'react-router';

import { IInvoiceStatusType, IInvoiceType } from '@/types';

import { ReportTypes } from '../../../../../../Reports/report.types';
import { useGetPrefundInfo } from '../queries/useGetPrefundInfo.query';
import { useGetPrefundInvoiceInfo } from '../queries/useGetPrefundInvoiceInfo.query';

import { useApproveInvoice } from './useApproveInvoice';
import { useDownloadInvoiceReport } from './useDownloadInvoiceReport';
import { useRepullInvoice } from './useRepullInvoice';

export const useFinOpsPrefund = () => {
  const { id } = useParams<{ id: string }>();

  const {
    formattedInvoiceTable,
    formattedData,
    isRejected,
    isLoading: invoiceLoading,
    refetch: refetchInvoice,
  } = useGetPrefundInvoiceInfo(id);

  const invoiceId = useMemo(() => formattedData.invoiceId, [formattedData]);
  const invoiceOrganizationId = useMemo(() => formattedData.organizationId, [formattedData]);

  const isReplenishment = useMemo(
    () =>
      formattedData.invoiceType === IInvoiceType.REPLENISHMENT ||
      formattedData.invoiceType === IInvoiceType.PLAN_LEVEL_REPLENISHMENT,
    [formattedData]
  );
  const isContribution = useMemo(
    () => formattedData.invoiceType === IInvoiceType.ORGANIZATION_LEVEL_CONTRIBUTIONS,
    [formattedData]
  );
  const isPrefund = useMemo(
    () =>
      formattedData.invoiceType === IInvoiceType.ORGANIZATION_LEVEL_PREFUND_REPLENISHMENT ||
      formattedData.invoiceType === IInvoiceType.PLAN_LEVEL_PREFUND_REPLENISHMENT,
    [formattedData]
  );

  const isEoyRecon = useMemo(
    () =>
      formattedData.invoiceType === IInvoiceType.EOY_PLAN_RECONCILIATION,
    [formattedData]
  );

  const {
    formattedPrefundTable,
    isLoading: prefundLoading,
    isPrefundExist,
    refetch: refetchPrefund,
    formattedData: prefundFormattedData,
  } = useGetPrefundInfo(id, isPrefund);

  const currentReportType = useMemo(() => {
    if (isReplenishment) return ReportTypes.Replenishment;
    if (isContribution) return ReportTypes.Contribution;
    if (isPrefund) return ReportTypes.Prefund;
    return null;
  }, [isContribution, isReplenishment, isPrefund]);

  const { setIsStartDownload, reportLoading, isServerError, setIsServerError } = useDownloadInvoiceReport({
    invoiceId,
    currentReportType,
    invoiceOrganizationId: invoiceOrganizationId || '',
  });

  const {
    handleRepullPrefund,
    handleRepullReplenishment,
    isPrefundAlertVisible,
    isReplenishmentAlertVisible,
    repullPrefundLoading,
    repullReplenishmentLoading,
  } = useRepullInvoice({
    invoiceId,
    isPrefund,
    isReplenishment,
    isRejected,
    repullManuallyAllowed: formattedData?.repullManuallyAllowed,
    isPrefundExist: !!isPrefundExist,
    refetchInvoice,
    refetchPrefund,
  });

  const {
    isInvoiceNeedApprove,
    isApproveLoading,
    isSuccess,
    handleApprove,
    handleRefetchStatus,
    amountInput,
    amount: approvedAmountInput,
    eoyReconPlanName,
  } = useApproveInvoice({
    invoiceType: formattedData.invoiceType as IInvoiceType,
    invoiceStatus: formattedData.invoiceStatusType as IInvoiceStatusType,
    initialAmount: formattedData.amount,
    invoiceId: formattedData.invoiceId,
    isPrefundExist: !!isPrefundExist,
    refetchInvoice,
    refetchPrefund,
  });

  const isLoading = useMemo(() => prefundLoading || invoiceLoading, [prefundLoading, invoiceLoading]);

  const isExportAvailable = useMemo(() => {
    if (!currentReportType) return false;
    if (isPrefund) return !!prefundFormattedData?.prefundHistoryId;
    return true;
  }, [currentReportType, isPrefund, prefundFormattedData]);

  return {
    formattedData,
    formattedInvoiceTable,
    isLoading,

    formattedPrefundTable,
    isPrefundExist,

    isExportAvailable,
    reportLoading,
    setIsStartDownload,
    isServerError,
    setIsServerError,

    handleRepullPrefund,
    handleRepullReplenishment,
    isPrefundAlertVisible,
    isReplenishmentAlertVisible,
    repullPrefundLoading,
    repullReplenishmentLoading,
    isEoyRecon,

    isInvoiceNeedApprove,
    isApproveLoading,
    isSuccess,
    handleApprove,
    handleRefetchStatus,
    amountInput,
    approvedAmountInput,
    eoyReconPlanName,
  };
};
